import { ReactElement, ReactNode, useRef } from "react";
import { Badge, Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { IoAdd, IoChevronForward } from "react-icons/io5";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PrimarySwitch } from "../../atoms/PrimarySwitch";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import {
  PhraseSettingFormData,
  RESERVED_SETTING_NAME,
} from "./phraseSettingForm";

function SettingRow(
  props: Readonly<{
    title: string;
    description: string;
    error?: string;
    value?: ReactNode;
    isDisabled: boolean;
    onClick: () => void;
    testId?: string;
  }>,
): ReactElement {
  const { title, description, error, value, isDisabled, onClick, testId } =
    props;
  return (
    <Flex
      align="center"
      gap={2}
      py={3}
      borderBottomWidth="1px"
      borderColor="chakra-border-color"
      opacity={isDisabled ? 0.5 : 1}
    >
      <Flex
        as="button"
        type="button"
        flex={1}
        align="center"
        textAlign="left"
        gap={2}
        disabled={isDisabled}
        cursor={isDisabled ? "default" : "pointer"}
        onClick={onClick}
        data-testid={testId}
      >
        <Box flex={1}>
          <Text>{title}</Text>
          {error && (
            <Text fontSize="xs" color="red.500">
              {error}
            </Text>
          )}
          <Text fontSize="xs" color="common_text.gray">
            {description}
          </Text>
        </Box>
        {value}
        {!isDisabled && <Icon as={IoChevronForward} color="common_text.gray" />}
      </Flex>
    </Flex>
  );
}

function SortableSettingRow(
  props: Readonly<{ id: string; children: ReactNode; isDisabled: boolean }>,
): ReactElement {
  const { id, children, isDisabled } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: isDisabled });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      bg="primary.bg_lv1"
      {...attributes}
      {...listeners}
    >
      {children}
    </Box>
  );
}

/*
 * 読み上げ文設定の一覧。設定の行はドラッグで並べ替えられる
 */
export function PhraseSettingList(
  props: Readonly<{
    formData: PhraseSettingFormData;
    useCompanySetting: boolean;
    allowOverride: boolean;
    onToggleCompanySetting: (checked: boolean) => void;
    onOpenSetting: (index: number | "new") => void;
    onOpenMinimum: () => void;
    onMove: (from: number, to: number) => void;
  }>,
): ReactElement {
  const {
    formData,
    useCompanySetting,
    allowOverride,
    onToggleCompanySetting,
    onOpenSetting,
    onOpenMinimum,
    onMove,
  } = props;
  const t = useModifiedTranslation();
  const settings = formData.phrases_settings;
  // タッチでは長押しでドラッグを始め、通常のスクロールを妨げない
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );
  // ドロップ直後のクリックで編集画面が開かないようにする
  const isDraggingRef = useRef(false);

  const handleDragEnd = (event: DragEndEvent): void => {
    setTimeout(() => {
      isDraggingRef.current = false;
    });
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = settings.findIndex((s) => s.id === active.id);
    const to = settings.findIndex((s) => s.id === over.id);
    if (from === -1 || to === -1) return;
    onMove(from, to);
  };

  return (
    <Box color="common_text.dark">
      <Flex align="center" gap={2} mb={2}>
        <Text fontWeight="bold">{t("PhraseSetting.phrase")}</Text>
        {useCompanySetting && (
          <Badge>{t("PhraseSetting.companySettingBadge")}</Badge>
        )}
      </Flex>
      <Box rounded="2xl" bg="primary.bg_lv1" px={4} py={1}>
        <Flex
          align="center"
          py={3}
          borderBottomWidth="1px"
          borderColor="chakra-border-color"
        >
          <Text flex={1}>{t("PhraseSetting.useCompanySetting")}</Text>
          <PrimarySwitch
            data-testid="phrase-setting-use-company"
            isChecked={useCompanySetting}
            isDisabled={!allowOverride}
            onChange={(e) => onToggleCompanySetting(e.target.checked)}
          />
        </Flex>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => {
            isDraggingRef.current = true;
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            isDraggingRef.current = false;
          }}
        >
          <SortableContext
            items={settings.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {settings.map((s, index) => {
              const timeRange =
                s.start_time === "" && s.end_time === ""
                  ? t("PhraseSetting.noTimeRange")
                  : `${s.start_time}〜${s.end_time}`;
              return (
                <SortableSettingRow
                  key={s.id}
                  id={s.id}
                  isDisabled={useCompanySetting}
                >
                  <SettingRow
                    title={s.setting_name}
                    description={`${timeRange} / ${t("PhraseSetting.numUtterance")} ${s.num_utterance}`}
                    error={
                      !useCompanySetting &&
                      s.setting_name === RESERVED_SETTING_NAME
                        ? t("PhraseSetting.error.reservedSettingName")
                        : undefined
                    }
                    isDisabled={useCompanySetting}
                    onClick={() => {
                      if (!isDraggingRef.current) onOpenSetting(index);
                    }}
                    testId={`phrase-setting-row-${index}`}
                  />
                </SortableSettingRow>
              );
            })}
          </SortableContext>
        </DndContext>

        <SettingRow
          title={t("PhraseSetting.minimumUtterances")}
          description={t("PhraseSetting.minimumUtterancesDescription")}
          value={<Text>{formData.minimum_utterances}</Text>}
          isDisabled={useCompanySetting}
          onClick={onOpenMinimum}
          testId="phrase-setting-row-minimum"
        />

        {!useCompanySetting && (
          <Button
            variant="ghost"
            color="primary.theme_lv1"
            w="full"
            my={2}
            leftIcon={<IoAdd />}
            onClick={() => onOpenSetting("new")}
            data-testid="phrase-setting-add"
          >
            {t("PhraseSetting.addSetting")}
          </Button>
        )}
      </Box>
    </Box>
  );
}
